/**
 * HTTP request (fetch) helpers.
 */

import { getCookie } from './cookie';

const JSON_MIME_TYPE = 'application/json';

/**
 * Wrapped fetch, used for exported GET and POST helpers.
 *
 * @ignore
 * @param {string} url - URL to fetch.
 * @param {string} method - HTTP method (GET, POST...).
 * @param {object} [extraHeaders] - Any extra headers to add.
 * @param {BodyInit} [body] - Body, e.g. for POST requests.
 * @returns {Promise} Resolved with the response data.
 */
async function _fetch(url, method, extraHeaders = {}, body) {
  const params = {
    method,
    body,
    credentials: 'same-origin',
    headers: {
      'X-REQUESTED-WITH': 'XMLHttpRequest',
      ...extraHeaders,
    },
  };

  const csrfCookie = getCookie('csrftoken');
  if (method !== 'GET' && csrfCookie) {
    params.headers['X-CSRFTOKEN'] = csrfCookie;
  }

  const response = await fetch(url, params);
  const responseContentType = response.headers.get('Content-Type');

  if (!response.ok) {
    throw new Error(`Got response ${response.status} ${response.statusText}`);
  }

  // SJR
  if (responseContentType === 'text/javascript') {
    const script = document.createElement('script');
    script.text = await response.text();
    document.head.appendChild(script).parentNode.removeChild(script);
    return null;
  }

  return responseContentType === JSON_MIME_TYPE
    ? response.json()
    : response.text();
}

async function _fetchJson(url, method, extraHeaders = {}, body) {
  return _fetch(
    url,
    method,
    {
      'Accept': JSON_MIME_TYPE,
      'Content-Type': JSON_MIME_TYPE,
      ...extraHeaders,
    },
    body,
  );
}

/**
 * Run a GET request.
 *
 * @param {string} url - URL to get.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @returns {Promise} Resolved with the response data.
 */
export function httpGet(url, extraHeaders = {}) {
  return _fetch(url, 'GET', extraHeaders);
}

/**
 * Run a POST request.
 *
 * @param {string} url - URL to get.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @param {BodyInit} [body] - POST body.
 * @returns {Promise} Resolved with the response data.
 */
export function httpPost(url, extraHeaders = {}, body) {
  return _fetch(url, 'POST', extraHeaders, body);
}
/**
 * Run a PUT request.
 *
 * @param {string} url - URL to get.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @param {BodyInit} [body] - PUT body.
 * @returns {Promise} Resolved with the response data.
 */
export function httpPut(url, extraHeaders = {}, body) {
  return _fetch(url, 'PUT', extraHeaders, body);
}

/**
 * Run a GET request to fetch JSON.
 *
 * Just like a regular fetch but with `application/json` headers set.
 *
 * @param {string} url - URL to get.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @returns {Promise} Resolved with the JSON response.
 */
export function httpGetJSON(url, extraHeaders = {}) {
  return _fetchJson(url, 'GET', extraHeaders);
}

/**
 * Run a JSON POST request.
 *
 * Just like a regular fetch but with `application/json` headers set.
 *
 * @param {string} url - URL to get.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @param {BodyInit} [body] - POST body.
 * @returns {Promise} Resolved with the JSON response.
 */
export function httpPostJSON(url, extraHeaders = {}, body) {
  return _fetchJson(url, 'POST', extraHeaders, body);
}

/**
 * Run a JSON PUT request.
 *
 * Just like a regular fetch but with `application/json` headers set.
 *
 * @param {string} url - URL to get.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @param {BodyInit} [body] - PUT body.
 * @returns {Promise} Resolved with the JSON response.
 */
export function httpPutJSON(url, extraHeaders = {}, body) {
  return _fetchJson(url, 'PUT', extraHeaders, body);
}

/**
 * Load an image.
 *
 * @param {string} src - Image URL.
 * @returns {Promise} Resolved or rejected with the image object, depending on
 *   if loading was successful.
 */
export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.onerror = () => {
      reject(img);
    };
    img.src = src;
  });
}

/**
 * Load a JavaScript file.
 *
 * @param {string} src - JavaScript file URL.
 * @returns {Promise} Resolved or rejected with the script tag object,
 *   depending on if loading was successful.
 */
export function loadJS(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');

    script.onload = () => {
      resolve(script);
    };
    script.onerror = () => {
      reject(script);
    };

    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  });
}
